/**
 * Toont de avatars van iedereen die dezelfde branch open heeft —
 * inclusief de huidige user, gemarkeerd met een dubbele ring zodat
 * je in één oogopslag ziet welke "jij" bent.
 *
 * Eigen avatar staat helemaal links (vast), gevolgd door peers
 * gesorteerd op connectionId. Voedt zich uit `subscribeAwareness`
 * — bij elke broadcast vanuit de DO wordt de balk verfrist.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveBilingual } from "../registry";
import { useChatStore } from "../state/chat";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { ChatPopup } from "./ChatPopup";
import type { AwarenessRecord } from "./CrdtProvider";
import { getMyConnectionId, setLocalAwareness, subscribeAwareness } from "./session";

export function PresenceBar() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const [records, setRecords] = useState<AwarenessRecord[]>([]);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const openPeer = useChatStore((s) => s.openPeer);
  const closeChat = useChatStore((s) => s.close);

  useEffect(() => {
    return subscribeAwareness(setRecords);
  }, []);

  // Zorg dat onze eigen email in de awareness-tafel staat zodat anderen
  // óók zien dat wij online zijn — al zit het vooral in de "user" die
  // de DO uit de auth-laag haalt. Hier vullen we daarbovenop een
  // displayName + kleur-hint als state, zodat peers iets visueel
  // hebben om aan op te hangen.
  useEffect(() => {
    if (!me?.email) return;
    setLocalAwareness({
      email: me.email,
      color: colorFor(me.email),
      // Waar we op dit moment naar kijken — de tooltip van een collega
      // toont dit als "Werkt in: …", zodat je weet of je 'm stoort in
      // hetzelfde diagram of ergens anders.
      activeDiagramId,
    });
  }, [me?.email, activeDiagramId]);

  // We tonen ALLE awareness-records — eigen verbinding gemarkeerd
  // met een dubbele ring zodat je weet welke "jij" bent. Filter op
  // connectionId (uniek per WS) zodat in dev-mode (alle tabs dezelfde
  // mock-user) elke tab een eigen avatar krijgt.
  const myId = getMyConnectionId();
  const myRecord = records.find((r) => r.connectionId === myId) ?? null;
  const peers = records.filter((r) => r.connectionId !== myId);

  // Niets te tonen tot we minstens onszelf in de awareness-tafel zien —
  // behalve wanneer er een gesprek openstaat. Tijdens een herverbinding is
  // de awareness-tabel even leeg, en zonder deze uitzondering verdween het
  // berichtvenster (met je halve zin erin) op zo'n moment van het scherm.
  if (!myRecord && peers.length === 0 && !openPeer) return null;

  const peerCount = peers.length;
  const tooltip =
    peerCount === 0 ? t("presence.onlyYou") : t("presence.youPlusOthers", { count: peerCount });

  return (
    <div className="relative flex items-center gap-1" title={tooltip}>
      {myRecord && (
        <Avatar key={myRecord.connectionId} email={myRecord.user} isMe record={myRecord} />
      )}
      {peers.slice(0, 5).map((r) => (
        // Key = connectionId (uniek per WS) zodat twee tabs van
        // dezelfde user niet conflicteren in React's reconciliation.
        <Avatar key={r.connectionId} email={r.user} record={r} />
      ))}
      {openPeer && <ChatPopup peerEmail={openPeer} onClose={closeChat} />}
      {peers.length > 5 && <span className="text-(--ld-muted) text-xs">+{peers.length - 5}</span>}
    </div>
  );
}

function Avatar({
  email,
  isMe = false,
  record,
}: {
  email: string;
  isMe?: boolean;
  record: AwarenessRecord;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const initial = email.charAt(0).toUpperCase();
  const unread = useChatStore((s) => s.unreadFrom.includes(email));
  const openWith = useChatStore((s) => s.openWith);

  // "Werkt in: …" onder het adres. De diagram-id komt uit awareness; de
  // naam zoeken we lokaal op, want die reist niet mee over de draad.
  const peerDiagramId =
    typeof record.state?.activeDiagramId === "string" ? record.state.activeDiagramId : null;
  const peerDiagram = peerDiagramId
    ? useWorkspaceData.getState().store?.getDiagram(peerDiagramId)
    : null;
  const base = isMe ? t("presence.meLabel", { email }) : email;
  const title = peerDiagram
    ? `${base}\n${t("presence.workingIn", { name: resolveBilingual(peerDiagram.name, lang) })}`
    : base;

  return (
    <span className="relative inline-flex">
      {/* Twee varianten met een letterlijke `role` in plaats van één met een
          uitdrukking: de eigen avatar is een plaatje, die van een collega is
          een knop. Bewust géén tabIndex — dat zou de tab-volgorde in de
          kopbalk veranderen, en daar hangt de toetsbesturing van de app aan. */}
      {isMe ? (
        <span
          role="img"
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-semibold text-white text-xs ${circleRing(true)}`}
          style={{ background: colorFor(email) }}
          title={title}
          aria-label={title}
        >
          {initial}
        </span>
      ) : (
        <span
          role="button"
          className={`inline-flex h-6 w-6 cursor-pointer touch-manipulation items-center justify-center rounded-full font-semibold text-white text-xs ${circleRing(false)}`}
          style={{ background: colorFor(email) }}
          title={title}
          aria-label={title}
          // Met een muis dubbelklik en niet enkelklik: de avatarbalk zit vlak
          // naast de branch-kiezer, en een enkele klik daar mag geen venster
          // openen. Op een aanraakscherm (iPad) maakt Safari van een dubbele
          // tik geen dubbelklik maar zoomt het; daar opent één tik de chat.
          onDoubleClick={() => openWith(email)}
          onPointerUp={(e) => {
            if (e.pointerType === "touch" || e.pointerType === "pen") openWith(email);
          }}
        >
          {initial}
        </span>
      )}
      {unread && (
        <span
          role="img"
          className="-bottom-0.5 -right-0.5 pointer-events-none absolute rounded-full bg-(--ld-surface) px-0.5 text-xs leading-none"
          title={t("chat.unread")}
          aria-label={t("chat.unread")}
        >
          ✉
        </span>
      )}
    </span>
  );
}

/** Eigen avatar krijgt een dubbele ring zodat je 'm in één oogopslag
 *  onderscheidt van die van een collega. */
function circleRing(isMe: boolean): string {
  return isMe ? "ring-2 ring-white outline outline-2 outline-(--ld-accent)" : "ring-2 ring-white";
}

/**
 * Deterministische kleur o.b.v. email — zo krijgt elke gebruiker
 * altijd dezelfde tint, ook over reconnects heen. Zelfde algoritme
 * als veel chat-apps gebruiken: hash → HSL met vaste S/L.
 */
function colorFor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i += 1) {
    hash = (hash * 31 + email.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 45%)`;
}
